import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Event, MessageActionType, UserAccessType, UserGroup, Message, AppComponentName, MessageType, MessageNoteCategory } from '@models';
import { ActionQueue, ActionQueueRules } from '@models/ActionQueue/ActionQueue.model';
import { ActionQueueLive } from '@models/ActionQueue/ActionQueueLive.model';
import { ActionQueueService } from '@services/actionQueue.service';
import { AppMessageService } from '@services/message.service';
import { UserService } from '@services/user.service';
import { ChangeLogNotesParams, ChangeLogNotesComponent } from 'app/components/messages/changelog-notes/changelognotes.components';
import { Authorization } from 'app/shared/classes/authorization';
import { CommonShared } from 'app/shared/classes/common';
import { json } from 'd3';

import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';


export interface ViewActionQueueParams {
  actionQueue: ActionQueue;
  userGroups: UserGroup[];
  associatedUserGroups: UserGroup[];
}

@Component({
  standalone: false,
  selector: 'view-action-queues',
  templateUrl: './view-action-queues.component.html',
  styleUrls: ['./view-action-queues.component.scss']
})

export class ViewActionQueueComponent implements OnInit {
  
  auth = new Authorization();
  common = new CommonShared()

  actionQueue: ActionQueue;
  associatedUserGroups: UserGroup[] = new Array<UserGroup>();
  userGroups: UserGroup[] = [];
  selectedUserGroup: UserGroup;

  modalDragOn: boolean = false;
  actionQueuesModalRef: BsModalRef;
  username = 'unknown';
  changeLogNotes = '';
  queueWaitingOrderNum: number[] = [];

  QueuedWaitingCols = [
    {field: 'status', header: 'Status', width: '8%'},
    {field: 'isManual', header: '', width: '2%'},
    {field: 'eventName', header: 'Event Name', width: '23%'},
    {field: 'actionType', header: 'Action(s)', width: '10%'},
    {field: 'createdDate', header: 'Queued (Mins)', width: '20%'},
    {field: 'runByDate', header: 'Run by Time', width: '15%'},
    {field: 'cancel', header: 'Cancel', width: '7%'},
    {field: 'release', header: 'RunNow', width: '9%'},
    {field: 'reorder', header: 'Reorder', width: '7%'}
  ];

  QueuedHistoryCols = [
    {field: 'status', header: 'Status', width: '11%'},
    {field: 'ruleUsed', header: 'Rule Used', width: '14%'},
    {field: 'isManual', header: '', width: '2%'},
    {field: 'eventName', header: 'Event Name', width: '22%'},
    {field: 'actionType', header: 'Action(s)', width: '13%'},
    {field: 'modifiedDate', header: 'Updated Date', width: '22%'},
    {field: 'timeInQueue', header: 'QTime', width: '9%'},
    {field: 'restore', header: 'Revert', width: '5%'},
  ];

  constructor(public bsModalRef: BsModalRef, private toastr: ToastrService, private modalService: BsModalService,
               public _userService: UserService, private _actionQueueService: ActionQueueService,
               public _appMessageService: AppMessageService, public confirmationService: ConfirmationService,) {
  }


  ngOnInit() {
    this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {
        this.username = results['value'];
        if (this.username != null && this.username !== '') {
          this.startUp();
        }
      },
      err => {
        this.toastr.error('Error unable to read current user\'s info');
      });
  }


  startUp() {
    this.loadInfoForActionQueue();
  }


  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }


  ngOnChanges() {
  }


  loadInfoForActionQueue() {
    try {
      this.actionQueue.rulesObj = JSON.parse(this.actionQueue.rules);
    } catch (e) {
      this.actionQueue.rulesObj = new ActionQueueRules();
    }

    var isDayTimeHours = this.isDayTimeHours(new Date(), this.actionQueue.rulesObj.maxWaitInQueueDayStartHour, this.actionQueue.rulesObj.maxWaitInQueueNightStartHour);

    this.actionQueue.queued.forEach(q => {
      if (q.status.toLowerCase() == 'waiting') {
        q.waitingMinutesInQueue = moment().diff(q.createdDate, 'minutes');

        q.runByDate =  (isDayTimeHours) ? moment(q.createdDate).add(this.actionQueue.rulesObj.maxWaitMinutesInQueueDay, 'minutes').toDate() :
                                          moment(q.createdDate).add(this.actionQueue.rulesObj.maxWaitMinutesInQueueNight, 'minutes').toDate();
      }
      
      q.totalMinutesInQueue = moment(q.activeDate ? q.activeDate : new Date()).diff(q.createdDate, 'minutes');

      if (q.totalMinutesInQueue < 0) {
        q.totalMinutesInQueue = moment(q.modifiedDate ? q.modifiedDate : new Date()).diff(q.createdDate, 'minutes');
      }
    });

    if (this.actionQueue.userGroupId == null || this.actionQueue.userGroupId == 0) {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id === -1);
    } else {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id === this.actionQueue.userGroupId);
    }

    this.queueWaitingOrderNum = this.actionQueue.queuedWaiting.map(x => x.orderNum);
  }


  isDayTimeHours(currentTime: Date, dayStartHour: number, nightStartHour: number): boolean {
    const currentHour = currentTime.getHours();

    if (nightStartHour >= dayStartHour)  // eg. 09 and 22
    {
        return (currentHour >= dayStartHour && currentHour < nightStartHour) ? true : false;
    }
    else                                // eg. 09 and 02
    {
        return (currentHour < dayStartHour && currentHour >= nightStartHour) ? false : true;
    }
  }


  updateActionQueue() {
  }


  createMessageData(msgActionType: MessageActionType, actionQueue: ActionQueue, msgNote: string): void {
    const message: Message = {
      id: 0,
      messageType: MessageType.Audit,
      appComponentName: AppComponentName.EventAndActions,
      actionType: msgActionType,
      appComponentItem: actionQueue.name,
      details: msgNote,
      userNotes: this.changeLogNotes,
      count: 1,
      acknowledged: false,
      userName: this.username,
      userGroupId: actionQueue.userGroupId
    };

    this._appMessageService.createAppMessage(message).subscribe(
      results => {
        if (results != null) {
          //this.toastr.success('Success');
        } else {
          //this.toastr.warning('Not found.');
        }
        return;
      },
      () => {
        //this.toastr.error('Error occurred while saving change log.');
      });
  }


  refreshWaitingAndHistoryQueues() {
    this.actionQueue.queuedWaiting = this.actionQueue.queued.filter(q => q.status.toLowerCase() == 'waiting');
    this.actionQueue.queuedHistory = this.actionQueue.queued.filter(q => q.status.toLowerCase() != 'waiting');

    this.common.sortActionQueueByColumn(this.actionQueue.queuedHistory, 'modifiedDate', false);
    this.common.sortActionQueueByColumn(this.actionQueue.queuedWaiting, 'orderNum', true);
    this.common.sortActionQueueByColumn(this.actionQueue.queued, 'createdDate', false);

    this.actionQueue.queued.forEach(x => {
      this.common.setStatusColorForActionQueueLiveItem(x);
    });
  }


  cancelQueueItem(queueItem: ActionQueueLive) {
    this.confirmationService.confirm({
      header: 'Cancel Confirmation',
      icon: 'glyphicon glyphicon-warning-sign',
      message: `Are you sure you want to cancel this queue item?`,
      key: 'cd5',
      accept: () => {
        this._actionQueueService.cancelQueuedItem(queueItem.id, 'Canceling queue item ...').subscribe(
          results => {
            if (results != null) {
              queueItem.status = 'canceled';
              this.refreshWaitingAndHistoryQueues();
              this.createMessageData(MessageActionType.Edited, this.actionQueue, 'Canceled queue item');
              this.toastr.success('Successfully canceled queue item.');
            } else {
              this.toastr.warning('Not found.');
            }
            return;
          },
          () => {
            this.toastr.error('Error occurred while canceling queue item.');
        });
      }
    });
  }


  restoreCanceledItem(queueItem: ActionQueueLive) {
    this._actionQueueService.restoreCanceledQueuedItem(queueItem.id, 'Restoring queue item ...').subscribe(
      results => {
        if (results != null) {
          queueItem.status = 'waiting';
          this.refreshWaitingAndHistoryQueues();
          this.createMessageData(MessageActionType.Edited, this.actionQueue, 'Restored queue item');
          this.toastr.success('Successfully restored queue item.');
        } else {
          this.toastr.warning('Not found.');
        }
        return;
      },
      () => {
        this.toastr.error('Error occurred while restoring queue item.');
    });
  }


  executeQueueItem(queueItem: ActionQueueLive) {
    this.confirmationService.confirm({
      header: 'Execute Confirmation',
      icon: 'glyphicon glyphicon-warning-sign',
      message: `Are you sure you want to run this queue item now?`,
      key: 'cd5',
      accept: () => {
        this._actionQueueService.executeQueueItem(queueItem.id, 'Executing queue item ...').subscribe(
          results => {
            if (results != null) {
              queueItem.status = 'active';
              this.refreshWaitingAndHistoryQueues();
              this.createMessageData(MessageActionType.Edited, this.actionQueue, 'Executed queue item');
              this.toastr.success('Successfully executed queue item.');
            } else {
              this.toastr.warning('Not found.');
            }
            return;
          },
          () => {
            this.toastr.error('Error occurred while executing queue item.');
        });
      }
    });
  }


  onQueueReorder(reorderEvent: ActionQueueLive) {
    this.actionQueue.queuedWaiting.forEach((item, index) => {
      if (item.orderNum != this.queueWaitingOrderNum[index]) {
        item.orderNum = this.queueWaitingOrderNum[index];
        this._actionQueueService.updateQueuedItem(item, '').subscribe(
          results => {
            if (results != null) {
              this.refreshWaitingAndHistoryQueues();
              this.createMessageData(MessageActionType.Edited, this.actionQueue, 'Updated queue order');
              this.toastr.success('Successfully updated queue order.');
            } else {
              this.toastr.warning('Not found.');
            }
            return;
          },
          () => {
            this.toastr.error('Error occurred while updating queue order.');
          });
      }
    });

  this.queueWaitingOrderNum = this.actionQueue.queuedWaiting.map(x => x.orderNum);
  }


  addNotes2ChangeLog() {
    this.changeLogNotes = '';

    const params: ChangeLogNotesParams = {
      changeLogNotes: this.changeLogNotes,
      userGroupId: this.selectedUserGroup.id,
      category: MessageNoteCategory.EventEdit
    };

    var modalRef = this.modalService.show(ChangeLogNotesComponent, { animated: false, backdrop: false, ignoreBackdropClick: true, class: 'modal-above-confirmdialog', initialState: params });
    modalRef.content.onChangeLogNotes.subscribe((value) => {
      this.changeLogNotes = value;
    });
  }


  getCreatedDateTooltip(queueItem: ActionQueueLive): string {
    let tooltip = `Queued: ${moment(queueItem.createdDate).format('MMM D, h:mm:ss A')}`;

    if (queueItem.activeDate && moment(queueItem.activeDate).diff(moment(queueItem.createdDate), 'minutes') >= 0) {
      tooltip += `\nActive: ${moment(queueItem.activeDate).format('MMM D, h:mm:ss A')}`;
    }
    else {
      tooltip += `\nActive: N/A`;
    }
    if (queueItem.modifiedDate) {
      tooltip += `\nModified: ${moment(queueItem.modifiedDate).format('MMM D, h:mm:ss A')}`;
    }
    return tooltip;
  }


}